// /admin/ventas-manuales
// Punto de venta para conocidos que quieren comprar pero no se hacen usuario.
// - Cliente guest (nombre + tel + email opcional, no crea auth user)
// - Buscador de catálogo → carrito con cant y precio editable
// - Toggle envío (sí/no + barrio + fecha + tarifa auto)
// - Pago: solo transferencia (evita comisión MP)
// - Toggle facturación (si off, respeta `pedidos.emitir_cfe=false` que corta el auto-CFE)
//
// El pedido queda en estado 'pendiente_pago'. Cuando se marca transferencia OK
// desde /admin/cobros-pendientes-transferencia, pasa a 'pagado' y (si emitir_cfe=true)
// se dispara el auto-CFE via trigger.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { calcularEnvioServer } from '../../lib/envios'

const VENTANAS = [
  { v: 'manana', l: 'Mañana (10–14hs)' },
  { v: 'tarde',  l: 'Tarde (14–18hs)' },
  { v: 'noche',  l: 'Tarde-noche (18–21hs)' },
]
const DIAS_MAP = {
  0: 'domingo', 1: 'lunes', 2: 'martes', 3: 'miercoles', 4: 'jueves', 5: 'viernes', 6: 'sabado',
}

const fmtMoney = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'
const hoyISO = () => new Date().toISOString().slice(0, 10)

export default function VentaManual() {
  const nav = useNavigate()

  // Modos: 'lista' (histórico) | 'nueva' (formulario de creación)
  const [modo, setModo] = useState('lista')

  return (
    <AdminLayout title={modo === 'nueva' ? 'Nueva venta manual' : 'Ventas manuales'}>
      <Estilos />
      {modo === 'lista'
        ? <Listado onNueva={() => setModo('nueva')} navToPedido={(id) => nav(`/admin/pedidos?highlight=${id}`)} />
        : <Formulario onCancelar={() => setModo('lista')} onCreado={() => setModo('lista')} />}
    </AdminLayout>
  )
}

// ────────────────────── LISTADO ──────────────────────
function Listado({ onNueva, navToPedido }) {
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase
      .from('pedidos')
      .select('id, numero_orden, comprador_nombre, comprador_telefono, comprador_email, comprador_documento, comprador_documento_tipo, comprador_profile_id, facturacion_razon_social, facturacion_email, monto_total, estado, metodo_pago, emitir_cfe, envio_es_en_ruta, fecha_entrega_programada, created_at, pagado_at, notas')
      .eq('tipo', 'compra_unica')
      .is('empresa_id', null)
      .order('created_at', { ascending: false })
      .limit(50)
    setPedidos(data || [])
    setLoading(false)
  }

  const [confirmando, setConfirmando] = useState(null) // id del pedido en el que estás confirmando
  const [editando, setEditando] = useState(null)       // pedido que se está editando (modal)

  async function marcarPagada(pedido) {
    if (!confirm(`¿Confirmar transferencia de ${pedido.comprador_nombre} por ${fmtMoney(pedido.monto_total)}?\n\n${pedido.emitir_cfe ? 'Se emitirá el CFE automáticamente.' : 'Sin factura (venta amiga).'}`)) return
    setConfirmando(pedido.id)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const nowIso = new Date().toISOString()
      const { error } = await supabase
        .from('pedidos')
        .update({
          estado: 'pagado',
          pagado_at: nowIso,
          pagado_confirmado_por_id: user?.id || null,
        })
        .eq('id', pedido.id)
      if (error) throw error
      await cargar()
    } catch (e) {
      alert('Error marcando pagada: ' + (e.message || String(e)))
    } finally {
      setConfirmando(null)
    }
  }

  return (
    <div className="vm-page">
      <div className="vm-topbar">
        <div>
          <h1>🛒 Ventas manuales</h1>
          <p className="muted">Ventas puntuales para gente que no tiene usuario. Pago por transferencia.</p>
        </div>
        <button className="vm-btn-primary" onClick={onNueva}>+ Nueva venta manual</button>
      </div>

      {loading && <div className="vm-loading">Cargando…</div>}

      {!loading && pedidos.length === 0 && (
        <div className="vm-empty">
          <div style={{ fontSize: 44 }}>🛒</div>
          <h3>Todavía no cargaste ninguna</h3>
          <p>Arrancá con el botón de arriba.</p>
        </div>
      )}

      {!loading && pedidos.length > 0 && (
        <div className="vm-table-wrap">
          <table className="vm-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>N° orden</th>
                <th>Cliente</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Entrega</th>
                <th>Estado</th>
                <th>Factura</th>
                <th style={{ textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map(p => (
                <tr key={p.id}>
                  <td onClick={() => navToPedido(p.id)} style={{ cursor: 'pointer' }}>{fmtFecha(p.created_at)}</td>
                  <td onClick={() => navToPedido(p.id)} style={{ cursor: 'pointer' }}><strong>{p.numero_orden}</strong></td>
                  <td className="vm-cel-cliente" onClick={() => navToPedido(p.id)} style={{ cursor: 'pointer' }}>
                    {p.comprador_nombre}
                    {p.comprador_profile_id && <span className="vm-chip-cli">cliente</span>}
                    {p.comprador_telefono && <div className="vm-cel-sub">📱 {p.comprador_telefono}</div>}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(p.monto_total)}</td>
                  <td>
                    {p.fecha_entrega_programada
                      ? <>{fmtFecha(p.fecha_entrega_programada)}<div className="vm-cel-sub">{p.envio_es_en_ruta ? '🟢 en ruta' : '🟡 fuera ruta'}</div></>
                      : <span className="muted">Retira</span>}
                  </td>
                  <td><EstadoBadge estado={p.estado} /></td>
                  <td>{p.emitir_cfe ? '✓ Sí' : <span className="muted">Sin ticket</span>}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {p.estado === 'pendiente_pago' ? (
                      <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="vm-btn-mini"
                          onClick={(e) => { e.stopPropagation(); setEditando(p) }}
                          title="Editar datos / ticket"
                        >
                          ✎ Editar
                        </button>
                        <button
                          className="vm-btn-pagar"
                          disabled={confirmando === p.id}
                          onClick={(e) => { e.stopPropagation(); marcarPagada(p) }}
                          title="Confirmar transferencia recibida"
                        >
                          {confirmando === p.id ? '…' : '✓ Marcar pagada'}
                        </button>
                      </span>
                    ) : p.estado === 'pagado' ? (
                      <span className="vm-cel-sub" style={{ color: '#166534' }}>✓ {fmtFecha(p.pagado_at)}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editando && (
        <EditModal
          pedido={editando}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); cargar() }}
        />
      )}
    </div>
  )
}

// ────────────────────── EDITAR VENTA (pendiente) ──────────────────────
function EditModal({ pedido, onClose, onSaved }) {
  const [f, setF] = useState({
    comprador_nombre: pedido.comprador_nombre || '',
    comprador_telefono: pedido.comprador_telefono || '',
    comprador_email: pedido.comprador_email || '',
    comprador_documento: pedido.comprador_documento || '',
    comprador_documento_tipo: pedido.comprador_documento_tipo === 'rut' ? 'rut' : 'ci',
    emitir_cfe: pedido.emitir_cfe !== false, // legacy sin ticket → permitimos prenderlo
    facturacion_razon_social: pedido.facturacion_razon_social || '',
    facturacion_email: pedido.facturacion_email || '',
    notas: pedido.notas || '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))

  async function guardar() {
    setErr(null)
    if (!f.comprador_nombre.trim()) return setErr('Falta el nombre')
    if (f.comprador_documento_tipo === 'rut' && !/^\d{12}$/.test((f.comprador_documento || '').replace(/\D/g, ''))) {
      return setErr('Para e-Factura el RUT debe tener 12 dígitos')
    }
    setSaving(true)
    try {
      const doc = (f.comprador_documento || '').replace(/\D/g, '') || null
      const { error } = await supabase.from('pedidos').update({
        comprador_nombre: f.comprador_nombre.trim(),
        comprador_telefono: f.comprador_telefono.trim() || null,
        comprador_email: f.comprador_email.trim() || null,
        comprador_documento: doc,
        comprador_documento_tipo: doc ? f.comprador_documento_tipo : null,
        emitir_cfe: f.emitir_cfe,
        facturacion_razon_social: f.emitir_cfe ? (f.facturacion_razon_social.trim() || f.comprador_nombre.trim()) : null,
        facturacion_rut: f.emitir_cfe && f.comprador_documento_tipo === 'rut' ? doc : null,
        facturacion_email: f.emitir_cfe ? (f.facturacion_email.trim() || f.comprador_email.trim() || null) : null,
        notas: f.notas.trim() || null,
      }).eq('id', pedido.id)
      if (error) throw error
      onSaved()
    } catch (e) {
      setErr('Error guardando: ' + (e.message || String(e))); setSaving(false)
    }
  }

  return (
    <div className="vm-modal-bg" onClick={onClose}>
      <div className="vm-modal" onClick={e => e.stopPropagation()}>
        <div className="vm-modal-head">
          <h3>✎ Editar venta · {pedido.numero_orden}</h3>
          <button className="vm-btn-mini" onClick={onClose}>✕</button>
        </div>

        <div className="vm-grid-2">
          <Field label="Nombre *"><input value={f.comprador_nombre} onChange={e => set('comprador_nombre', e.target.value)} /></Field>
          <Field label="Teléfono"><input value={f.comprador_telefono} onChange={e => set('comprador_telefono', e.target.value)} /></Field>
        </div>
        <div className="vm-grid-2">
          <Field label="Email"><input type="email" value={f.comprador_email} onChange={e => set('comprador_email', e.target.value)} /></Field>
          <Field label="Cédula / documento"><input value={f.comprador_documento} onChange={e => set('comprador_documento', e.target.value)} placeholder="1.234.567-8" /></Field>
        </div>

        <label className="vm-check">
          <input type="checkbox" checked={f.emitir_cfe} onChange={e => set('emitir_cfe', e.target.checked)} />
          <span>Emitir comprobante (e-Ticket o e-Factura) al pagar</span>
        </label>

        {f.emitir_cfe && (
          <>
            <div className="vm-grid-2">
              <Field label="Tipo de comprobante">
                <select value={f.comprador_documento_tipo} onChange={e => set('comprador_documento_tipo', e.target.value)}>
                  <option value="ci">e-Ticket (consumidor final)</option>
                  <option value="rut">e-Factura (empresa · RUT)</option>
                </select>
              </Field>
              <Field label="Email del comprobante"><input type="email" value={f.facturacion_email} onChange={e => set('facturacion_email', e.target.value)} placeholder="factura@ejemplo.com" /></Field>
            </div>
            {f.comprador_documento_tipo === 'rut' && (
              <Field label="Razón social"><input value={f.facturacion_razon_social} onChange={e => set('facturacion_razon_social', e.target.value)} placeholder="Empresa SA" /></Field>
            )}
          </>
        )}

        <Field label="Notas"><textarea rows={2} value={f.notas} onChange={e => set('notas', e.target.value)} /></Field>

        {err && <div className="vm-modal-err">{err}</div>}
        <div className="vm-modal-foot">
          <button className="vm-btn-mini" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="vm-btn-primary" onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
        </div>
      </div>
    </div>
  )
}

function EstadoBadge({ estado }) {
  const map = {
    borrador:         { bg: '#f1f5f9', c: '#475569', l: 'Borrador' },
    pendiente_pago:   { bg: '#fef3c7', c: '#92400e', l: 'Pendiente pago' },
    pagado:           { bg: '#dcfce7', c: '#166534', l: 'Pagado' },
    en_armado:        { bg: '#e0f2fe', c: '#075985', l: 'En armado' },
    en_ruta:          { bg: '#e0e7ff', c: '#3730a3', l: 'En ruta' },
    entregado:        { bg: '#d1fae5', c: '#065f46', l: 'Entregado' },
    cancelado:        { bg: '#fee2e2', c: '#991b1b', l: 'Cancelado' },
  }
  const s = map[estado] || map.borrador
  return <span style={{ display: 'inline-block', padding: '3px 9px', background: s.bg, color: s.c, borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.l}</span>
}

// ────────────────────── FORMULARIO ──────────────────────
// Auto-save del borrador en localStorage (persiste si el user cierra la pestaña o navega).
// Se limpia solo cuando se confirma la venta con éxito, o desde el botón "🗑 Descartar borrador".
const BORRADOR_KEY = 'limpack.venta-manual.borrador.v1'
const FORM_DEFAULT = {
  comprador_profile_id: null, // si es cliente registrado, lo vincula a su cuenta
  comprador_nombre: '', comprador_telefono: '', comprador_email: '',
  comprador_documento: '', comprador_documento_tipo: 'ci',
  con_envio: true, entrega_departamento: 'Montevideo', entrega_barrio_id: '', entrega_direccion: '', entrega_apto: '',
  fecha_entrega: hoyISO(), ventana_horaria: 'manana',
  emitir_cfe: true, facturacion_razon_social: '', facturacion_rut: '', facturacion_email: '',
  habilitar_transferencia: true,
  envio_override: false, costo_envio_manual: 0,
  descuento_pct: 0, // NUEVO: descuento global sobre subtotal productos
  notas: '',
}

function cargarBorrador() {
  try {
    const raw = localStorage.getItem(BORRADOR_KEY)
    if (!raw) return null
    const b = JSON.parse(raw)
    return b && typeof b === 'object' ? b : null
  } catch { return null }
}

function Formulario({ onCancelar, onCreado }) {
  const [cargando, setCargando] = useState(true)
  const [productos, setProductos] = useState([])
  const [barrios, setBarrios] = useState([])
  const [tarifas, setTarifas] = useState({})
  const [error, setError] = useState(null)
  const [creando, setCreando] = useState(false)
  const [exito, setExito] = useState(null)

  // Restaurar borrador si existe
  const borradorInicial = useMemo(() => cargarBorrador(), [])

  // Form state (mergea defaults + borrador previo)
  const [form, setForm] = useState({
    ...FORM_DEFAULT,
    // Asegurar que fecha_entrega no sea del pasado si restauro un borrador viejo
    ...(borradorInicial?.form || {}),
    ...(borradorInicial?.form?.fecha_entrega && borradorInicial.form.fecha_entrega < hoyISO()
      ? { fecha_entrega: hoyISO() }
      : {}),
  })
  const [carrito, setCarrito] = useState(borradorInicial?.carrito || [])
  const [buscador, setBuscador] = useState('')
  const [regaloManual, setRegaloManual] = useState('')
  const [borradorGuardado, setBorradorGuardado] = useState(!!borradorInicial)

  useEffect(() => {
    (async () => {
      setCargando(true)
      const [{ data: prods }, { data: brs }, { data: tfs }] = await Promise.all([
        supabase.from('productos')
          .select('id, sku, nombre, marca, presentacion, precio_publico, iva_pct, stock_actual, stock_reservado')
          .eq('activo', true).eq('publicado', true).eq('pausado', false).is('deleted_at', null)
          .order('marca').order('nombre'),
        supabase.from('barrios').select('id, nombre, dias, departamento').eq('activo', true).order('nombre'),
        supabase.from('envio_tarifas').select('clave, monto'),
      ])
      setProductos(prods || [])
      setBarrios(brs || [])
      const tfIdx = {}; (tfs || []).forEach(t => { tfIdx[t.clave] = Number(t.monto) || 0 })
      setTarifas(tfIdx)
      setCargando(false)
    })()
  }, [])

  // ── Auto-save borrador (debounced 500ms) ──
  useEffect(() => {
    // No guardar si está vacío del todo (evita crear borrador fantasma)
    const vacio = !form.comprador_nombre && carrito.length === 0
      && !form.entrega_direccion && !form.comprador_telefono
    if (vacio) return
    const t = setTimeout(() => {
      try {
        localStorage.setItem(BORRADOR_KEY, JSON.stringify({
          form, carrito, guardado_at: new Date().toISOString(),
        }))
        setBorradorGuardado(true)
      } catch { /* localStorage lleno u otro fallo — ignorable */ }
    }, 500)
    return () => clearTimeout(t)
  }, [form, carrito])

  function descartarBorrador() {
    if (!confirm('¿Descartar el borrador? Se pierde todo lo cargado.')) return
    try { localStorage.removeItem(BORRADOR_KEY) } catch {}
    setForm(FORM_DEFAULT)
    setCarrito([])
    setBorradorGuardado(false)
  }

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  // ── Cliente registrado: buscar en profiles (role=cliente) y tomar sus datos ──
  const [esCliente, setEsCliente] = useState(!!(borradorInicial?.form?.comprador_profile_id))
  const [busqCliente, setBusqCliente] = useState('')
  const [resCliente, setResCliente] = useState([])
  const [buscandoCli, setBuscandoCli] = useState(false)

  useEffect(() => {
    const q = busqCliente.trim().replace(/[,()]/g, ' ')
    if (!esCliente || q.length < 2) { setResCliente([]); setBuscandoCli(false); return }
    let cancel = false
    setBuscandoCli(true)
    const t = setTimeout(async () => {
      const { data } = await supabase.from('profiles')
        .select('id, nombre, phone, email, documento, documento_tipo, direccion, apto, barrio_id')
        .eq('role', 'cliente')
        .or(`nombre.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${q}%`)
        .order('nombre').limit(8)
      if (!cancel) { setResCliente(data || []); setBuscandoCli(false) }
    }, 300)
    return () => { cancel = true; clearTimeout(t) }
  }, [busqCliente, esCliente])

  function elegirCliente(c) {
    setForm(f => ({
      ...f,
      comprador_profile_id: c.id,
      comprador_nombre: c.nombre || '',
      comprador_telefono: c.phone || '',
      comprador_email: c.email || '',
      comprador_documento: c.documento || '',
      comprador_documento_tipo: (c.documento_tipo === 'rut' ? 'rut' : 'ci'),
      ...(c.barrio_id ? { con_envio: true, entrega_barrio_id: c.barrio_id, entrega_departamento: (barrios.find(b => b.id === c.barrio_id)?.departamento || 'Montevideo') } : {}),
      ...(c.direccion ? { entrega_direccion: c.direccion } : {}),
      ...(c.apto ? { entrega_apto: c.apto } : {}),
    }))
    setBusqCliente(''); setResCliente([])
  }

  function desvincularCliente() {
    setForm(f => ({ ...f, comprador_profile_id: null }))
    setBusqCliente(''); setResCliente([])
  }

  // ── Carrito ──
  function agregarProducto(p) {
    setCarrito(c => {
      const existe = c.find(x => x.producto_id === p.id)
      if (existe) return c.map(x => x.producto_id === p.id ? { ...x, cantidad: x.cantidad + 1 } : x)
      return [...c, {
        producto_id: p.id,
        sku: p.sku,
        nombre: p.nombre,
        marca: p.marca,
        presentacion: p.presentacion,
        iva_pct: Number(p.iva_pct ?? 22),
        cantidad: 1,
        precio_unitario: Number(p.precio_publico) || 0,
        es_regalo: false,
      }]
    })
    setBuscador('')
  }
  function actualizarLinea(pid, campo, valor) {
    setCarrito(c => c.map(x => x.producto_id === pid ? { ...x, [campo]: valor } : x))
  }
  function quitarLinea(pid) {
    setCarrito(c => c.filter(x => x.producto_id !== pid))
  }
  // Marca/desmarca una línea del catálogo como regalo ($0, no suma al total, sí descuenta stock por su sku).
  function toggleRegalo(pid) {
    setCarrito(c => c.map(x => x.producto_id === pid ? { ...x, es_regalo: !x.es_regalo } : x))
  }
  // Regalo escrito a mano: algo que no está en el catálogo / sin stock. No descuenta stock (no tiene sku).
  function agregarRegaloManual() {
    const t = regaloManual.trim()
    if (!t) return
    setCarrito(c => [...c, {
      producto_id: `regalo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      sku: null,
      nombre: t,
      marca: '',
      presentacion: '',
      iva_pct: 0,
      cantidad: 1,
      precio_unitario: 0,
      es_regalo: true,
      manual: true,
    }])
    setRegaloManual('')
  }

  const productosFiltrados = useMemo(() => {
    if (!buscador.trim()) return []
    const q = buscador.toLowerCase()
    return productos.filter(p =>
      (p.nombre || '').toLowerCase().includes(q) ||
      (p.marca || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q)
    ).slice(0, 12)
  }, [productos, buscador])

  // ── Envío + totales ──
  const barrioElegido = useMemo(() => barrios.find(b => b.id === form.entrega_barrio_id), [barrios, form.entrega_barrio_id])
  // Departamento → filtra el barrio (Montevideo primero). Barrios de Canelones (Ciudad de la Costa / barrio privado) viven en la misma tabla con departamento.
  const departamentos = useMemo(() => {
    const set = [...new Set(barrios.map(b => b.departamento || 'Montevideo'))]
    return set.sort((a, b) => (a === 'Montevideo' ? -1 : b === 'Montevideo' ? 1 : a.localeCompare(b)))
  }, [barrios])
  const barriosFiltrados = useMemo(() => barrios.filter(b => (b.departamento || 'Montevideo') === form.entrega_departamento), [barrios, form.entrega_departamento])
  const fechaInfo = useMemo(() => {
    if (!form.fecha_entrega) return null
    const d = new Date(form.fecha_entrega + 'T00:00:00')
    const diaSem = DIAS_MAP[d.getDay()]
    const enRuta = barrioElegido?.dias?.includes(diaSem) || false
    return { diaSem, enRuta, barrio: barrioElegido?.nombre }
  }, [form.fecha_entrega, barrioElegido])

  const subtotalBruto = useMemo(() => carrito.reduce((s, x) => s + (x.es_regalo ? 0 : Number(x.cantidad) * Number(x.precio_unitario)), 0), [carrito])

  // Descuento global (%) sobre subtotal de productos (no aplica al envío)
  const descuentoPct = Math.max(0, Math.min(100, Number(form.descuento_pct) || 0))
  const descuentoMonto = Math.round((subtotalBruto * descuentoPct / 100) * 100) / 100
  const subtotal = Math.round((subtotalBruto - descuentoMonto) * 100) / 100

  // 2026-08-27: envío alineado a LimpackFlex (por zona) via la función canónica calcular_envio_para_usuario
  // (misma que la tienda). Reemplaza el modelo viejo por escenario (tienda chica/grande, en-ruta/fuera-ruta).
  const [costoEnvioAuto, setCostoEnvioAuto] = useState(0)
  useEffect(() => {
    let cancel = false
    if (!form.con_envio || !form.entrega_barrio_id || subtotal <= 0) { setCostoEnvioAuto(0); return }
    calcularEnvioServer({ tipo: 'productos_sueltos', barrioId: form.entrega_barrio_id, fecha: form.fecha_entrega || hoyISO(), monto: subtotal })
      .then(r => { if (!cancel) setCostoEnvioAuto(Number(r?.costoEnvio || 0)) })
      .catch(() => { if (!cancel) setCostoEnvioAuto(0) })
    return () => { cancel = true }
  }, [form.con_envio, form.entrega_barrio_id, form.fecha_entrega, subtotal])

  const costoEnvio = form.envio_override ? Number(form.costo_envio_manual) || 0 : costoEnvioAuto
  const total = subtotal + costoEnvio

  async function crear() {
    setError(null)
    // Validaciones
    if (!form.comprador_nombre.trim()) return setError('Falta el nombre del cliente')
    if (carrito.length === 0) return setError('Agregá al menos un producto')
    if (carrito.every(x => x.es_regalo)) return setError('Agregá al menos un producto cobrado (el regalo va a $0)')
    if (subtotal <= 0) return setError('Subtotal inválido')
    if (form.con_envio) {
      if (!form.entrega_barrio_id) return setError('Elegí el barrio de entrega')
      if (!form.entrega_direccion.trim()) return setError('Falta la dirección de entrega')
      if (!form.fecha_entrega) return setError('Falta fecha de entrega')
    }
    if (form.comprador_documento_tipo === 'rut' && !/^\d{12}$/.test((form.comprador_documento || '').replace(/\D/g, ''))) {
      return setError('Para e-Factura el RUT debe tener 12 dígitos')
    }

    setCreando(true)
    try {
      const { data: numOrden, error: nErr } = await supabase.rpc('generar_numero_orden')
      if (nErr) throw new Error('No se pudo generar número de orden: ' + nErr.message)

      // Preparar items para pedidos.items (jsonb).
      // Biller.uy rechaza precios negativos → aplicamos el descuento al
      // precio_unitario y agregamos "(dto X%)" al NOMBRE del producto para
      // que el cliente vea el descuento reflejado directamente en la factura.
      const factor = descuentoPct > 0 ? (1 - descuentoPct / 100) : 1
      const items = carrito.map(x => {
        const nombreBase = [x.marca, x.nombre, x.presentacion].filter(Boolean).join(' · ')
        // Regalo: va al CFE a $0 y NO lleva descuento. Si es de catálogo mantiene sku (descuenta stock);
        // si es manual, sku/producto_id null (no descuenta stock, no es un producto trackeado).
        if (x.es_regalo) {
          const nombreRegalo = `${nombreBase} (REGALO)`
          return {
            tipo: 'producto',
            producto_id: x.manual ? null : x.producto_id,
            sku: x.manual ? null : x.sku,
            nombre: nombreRegalo,
            descripcion: nombreRegalo,
            cantidad: Number(x.cantidad),
            precio_unitario: 0,
            iva_pct: Number(x.iva_pct) || 0,
            es_regalo: true,
            subtotal: 0,
          }
        }
        const nombreFinal = descuentoPct > 0
          ? `${nombreBase} (dto ${descuentoPct}%)`
          : nombreBase
        const precioFinal = Math.round(Number(x.precio_unitario) * factor * 100) / 100
        return {
          tipo: 'producto',
          producto_id: x.producto_id,
          sku: x.sku,
          nombre: nombreFinal,
          descripcion: nombreFinal,
          cantidad: Number(x.cantidad),
          precio_unitario: precioFinal,
          precio_original: descuentoPct > 0 ? Number(x.precio_unitario) : undefined,
          descuento_pct: descuentoPct > 0 ? descuentoPct : undefined,
          iva_pct: Number(x.iva_pct),
          subtotal: Math.round(Number(x.cantidad) * precioFinal * 100) / 100,
        }
      })

      // Fecha corte D-1 16hs si va con envío. Si retira, uso "hoy".
      const fechaEntrega = form.con_envio
        ? new Date(form.fecha_entrega + 'T00:00:00')
        : new Date()
      const fechaCorte = new Date(fechaEntrega)
      if (form.con_envio) {
        fechaCorte.setDate(fechaCorte.getDate() - 1)
        fechaCorte.setHours(16, 0, 0, 0)
      }
      const diaSemana = DIAS_MAP[fechaEntrega.getDay()]

      // Notas: dejar rastro claro
      const regalos = carrito.filter(x => x.es_regalo)
      const notas = [
        `🛒 Venta manual (canal directo/WA, sin usuario)`,
        form.emitir_cfe ? '📄 Facturación: SÍ' : '⚠ SIN FACTURA (venta amiga)',
        !form.con_envio ? '📦 Retira en depósito Cavia' : null,
        descuentoPct > 0 ? `🏷 Descuento global aplicado: ${descuentoPct.toFixed(2)}% (−${fmtMoney(descuentoMonto)})` : null,
        regalos.length > 0 ? `🎁 Regalo(s): ${regalos.map(r => `${[r.marca, r.nombre].filter(Boolean).join(' ')}${r.cantidad > 1 ? ` x${r.cantidad}` : ''}${r.manual ? ' (a mano)' : ''}`).join(', ')}` : null,
        form.notas || null,
      ].filter(Boolean).join('\n')

      const payload = {
        numero_orden: numOrden,
        comprador_profile_id: form.comprador_profile_id || null,  // vinculado si es cliente registrado
        comprador_nombre: form.comprador_nombre.trim(),
        comprador_telefono: form.comprador_telefono.trim() || null,
        comprador_email: form.comprador_email.trim() || null,
        comprador_documento: form.comprador_documento.replace(/\D/g, '') || null,
        comprador_documento_tipo: form.comprador_documento ? form.comprador_documento_tipo : null,
        tipo: 'compra_unica',
        items,
        entrega_barrio_id: form.con_envio ? form.entrega_barrio_id : null,
        entrega_direccion: form.con_envio ? form.entrega_direccion.trim() : 'RETIRA EN DEPÓSITO — Cavia 2858',
        entrega_apto: form.entrega_apto.trim() || null,
        entrega_dia_semana: diaSemana,
        entrega_ventana: form.ventana_horaria,
        monto_subtotal: subtotal,
        costo_envio: costoEnvio,
        monto_total: total,
        envio_es_en_ruta: form.con_envio ? (fechaInfo?.enRuta || false) : true, // retira = "en ruta" (no fuera)
        fecha_entrega_programada: form.con_envio ? form.fecha_entrega : hoyISO(),
        fecha_corte: fechaCorte.toISOString(),
        estado: 'pendiente_pago',
        metodo_pago: 'transferencia',
        condicion_pago: 'prepago',
        emitir_cfe: true,  // toda venta manual emite comprobante (e-Ticket o e-Factura)
        facturacion_razon_social: form.facturacion_razon_social.trim() || form.comprador_nombre.trim(),
        facturacion_rut: form.comprador_documento_tipo === 'rut' ? form.comprador_documento.replace(/\D/g, '') : null,
        facturacion_email: form.facturacion_email.trim() || form.comprador_email.trim() || null,
        notas,
      }

      const { data: nuevo, error: insErr } = await supabase
        .from('pedidos')
        .insert(payload)
        .select('id, numero_orden')
        .single()
      if (insErr) throw insErr

      // Venta creada OK → borrar el borrador auto-guardado
      try { localStorage.removeItem(BORRADOR_KEY) } catch {}
      setBorradorGuardado(false)

      setExito({ id: nuevo.id, numero_orden: nuevo.numero_orden, total })
    } catch (err) {
      setError(err.message || 'Error creando la venta manual')
    } finally {
      setCreando(false)
    }
  }

  if (cargando) return <div className="vm-loading">Cargando…</div>

  if (exito) {
    return (
      <div className="vm-exito">
        <div style={{ fontSize: 56, color: '#16A34A' }}>✓</div>
        <h2>Venta Nº {exito.numero_orden} creada</h2>
        <p>Total: <strong>{fmtMoney(exito.total)}</strong></p>
        <p className="muted">Quedó en estado <strong>pendiente_pago</strong>. Cuando confirmes la transferencia desde <code>/admin/cobros-pendientes-transferencia</code> pasa a pagado{form.emitir_cfe ? ' y se emite el CFE automático.' : ' (sin factura, como marcaste).'}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          <button className="vm-btn-secondary" onClick={onCreado}>← Volver al listado</button>
          <button className="vm-btn-primary" onClick={() => {
            setExito(null); setCarrito([]); setForm(FORM_DEFAULT); setBorradorGuardado(false)
          }}>+ Nueva venta</button>
        </div>
      </div>
    )
  }

  return (
    <div className="vm-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <button className="vm-back" onClick={onCancelar}>← Volver a Ventas manuales</button>
        {borradorGuardado && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="vm-hint ok" style={{ margin: 0 }}>💾 Borrador guardado</span>
            <button className="vm-link-danger" onClick={descartarBorrador} title="Descartar borrador">🗑</button>
          </div>
        )}
      </div>
      <h1>🛒 Nueva venta manual</h1>
      <p className="muted">Cargá los datos del cliente y los productos. El cliente no queda registrado como usuario del sistema. Si te salís, el borrador se guarda solo y lo retomás cuando vuelvas.</p>

      {/* Cliente */}
      <section className="vm-card">
        <h3>👤 Cliente</h3>

        {/* ¿Es cliente registrado? → busca en profiles y toma sus datos */}
        <label className="vm-check">
          <input type="checkbox" checked={esCliente} onChange={e => { setEsCliente(e.target.checked); if (!e.target.checked) desvincularCliente() }} />
          <span>Es cliente registrado (buscar y tomar sus datos)</span>
        </label>

        {esCliente && !form.comprador_profile_id && (
          <div className="vm-cli-search">
            <Field label="Buscar cliente (nombre, email o teléfono)">
              <input value={busqCliente} onChange={e => setBusqCliente(e.target.value)} placeholder="Ej: Andrea, andrea@…, 099…" autoComplete="off" />
            </Field>
            {buscandoCli && <div className="vm-cel-sub" style={{ padding: '4px 2px' }}>Buscando…</div>}
            {resCliente.length > 0 && (
              <div className="vm-search-results">
                {resCliente.map(c => (
                  <button key={c.id} type="button" className="vm-search-item" onClick={() => elegirCliente(c)}>
                    <div>
                      <strong>{c.nombre || 'Sin nombre'}</strong>
                      <div className="vm-cel-sub">{[c.email, c.phone].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!buscandoCli && busqCliente.trim().length >= 2 && resCliente.length === 0 && (
              <div className="vm-cel-sub" style={{ padding: '4px 2px' }}>Sin resultados. Si no está registrado, destildá la casilla y cargalo como venta suelta.</div>
            )}
          </div>
        )}

        {form.comprador_profile_id && (
          <div className={`vm-cli-linked ${form.comprador_documento ? '' : 'vm-cli-linked--warn'}`}>
            <span>✓ Cliente vinculado — el pedido queda en su cuenta. {form.comprador_documento ? `Cédula: ${form.comprador_documento}` : '⚠ Sin cédula guardada en su perfil — cargala abajo para la E-ticket.'}</span>
            <button type="button" className="vm-btn-mini" onClick={desvincularCliente}>Desvincular</button>
          </div>
        )}

        <div className="vm-grid-2">
          <Field label="Nombre *"><input value={form.comprador_nombre} onChange={e => setF('comprador_nombre', e.target.value)} placeholder="Andrea Pérez" /></Field>
          <Field label="Teléfono"><input value={form.comprador_telefono} onChange={e => setF('comprador_telefono', e.target.value)} placeholder="+598 99 123 456" /></Field>
        </div>
        <div className="vm-grid-2">
          <Field label="Email (opcional)"><input type="email" value={form.comprador_email} onChange={e => setF('comprador_email', e.target.value)} placeholder="para mandarle CFE si aplica" /></Field>
          <Field label="Cédula (para la E-ticket)"><input value={form.comprador_documento} onChange={e => setF('comprador_documento', e.target.value)} placeholder="1.234.567-8" inputMode="numeric" /></Field>
        </div>
      </section>

      {/* Productos */}
      <section className="vm-card">
        <h3>📦 Productos</h3>
        <Field label="Buscar producto (por nombre, marca o SKU)">
          <input value={buscador} onChange={e => setBuscador(e.target.value)} placeholder="Ej: cottonmax, jabón, papel…" />
        </Field>
        {productosFiltrados.length > 0 && (
          <div className="vm-search-results">
            {productosFiltrados.map(p => (
              <button key={p.id} className="vm-search-item" onClick={() => agregarProducto(p)}>
                <div>
                  <strong>{p.marca}</strong> · {p.nombre}
                  <div className="vm-cel-sub">{p.sku} · {p.presentacion}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700 }}>{fmtMoney(p.precio_publico)}</div>
                  <div className="vm-cel-sub">stock: {(p.stock_actual || 0) - (p.stock_reservado || 0)}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {carrito.length === 0 && <p className="muted" style={{ marginTop: 12 }}>Todavía no agregaste productos.</p>}
        {carrito.length > 0 && (
          <table className="vm-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th style={{ width: 80 }}>Cant.</th>
                <th style={{ width: 110 }}>Precio unit.</th>
                <th style={{ width: 110, textAlign: 'right' }}>Subtotal</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {carrito.map(x => (
                <tr key={x.producto_id} style={x.es_regalo ? { background: '#F0FDF4' } : null}>
                  <td>
                    {x.es_regalo && <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 800, color: '#166534', background: '#DCFCE7', borderRadius: 999, padding: '1px 8px', marginRight: 6 }}>🎁 REGALO</span>}
                    <strong>{x.marca}</strong>{x.marca ? ' · ' : ''}{x.nombre}
                    <div className="vm-cel-sub">{x.manual ? 'escrito a mano · no descuenta stock' : `${x.sku} · ${x.presentacion} · IVA ${x.iva_pct}%`}</div>
                  </td>
                  <td>
                    <input type="number" min="1" step="1" value={x.cantidad}
                      onChange={e => actualizarLinea(x.producto_id, 'cantidad', Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ width: '100%', padding: 6 }} />
                  </td>
                  <td>
                    {x.es_regalo
                      ? <span className="vm-cel-sub">$0 (regalo)</span>
                      : <input type="number" min="0" step="0.01" value={x.precio_unitario}
                          onChange={e => actualizarLinea(x.producto_id, 'precio_unitario', Number(e.target.value) || 0)}
                          style={{ width: '100%', padding: 6 }} />}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {x.es_regalo ? fmtMoney(0) : fmtMoney(x.cantidad * x.precio_unitario)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {!x.manual && (
                      <button className="vm-link" title={x.es_regalo ? 'Volver a cobrar' : 'Marcar como regalo ($0)'}
                        onClick={() => toggleRegalo(x.producto_id)} style={{ marginRight: 6 }}>
                        {x.es_regalo ? '↺' : '🎁'}
                      </button>
                    )}
                    <button className="vm-link-danger" onClick={() => quitarLinea(x.producto_id)}>✕</button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Subtotal</td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: '#1A3461' }}>{fmtMoney(subtotal)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Regalo escrito a mano — para algo que no está en el catálogo o sin stock */}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #CBD5E1' }}>
          <label style={{ fontWeight: 700, fontSize: 13, color: '#1A3461' }}>🎁 Agregar regalo a mano</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <input
              value={regaloManual}
              onChange={e => setRegaloManual(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregarRegaloManual() } }}
              placeholder="Ej: Esponja de regalo, muestra de suavizante…"
              style={{ flex: 1, padding: 8 }} />
            <button type="button" onClick={agregarRegaloManual}
              style={{ padding: '8px 14px', background: '#1A3461', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Agregar regalo
            </button>
          </div>
          <div className="vm-hint">Va en la factura a $0 y no descuenta stock (no es un producto del catálogo). Para regalar un producto del catálogo y que baje del stock, agregalo arriba y tocá 🎁.</div>
        </div>
      </section>

      {/* Envío */}
      <section className="vm-card">
        <h3>🚚 Envío</h3>
        <label className="vm-check">
          <input type="checkbox" checked={form.con_envio} onChange={e => setF('con_envio', e.target.checked)} />
          <span>¿Va con envío?</span>
          {!form.con_envio && <span className="vm-hint">Retira en depósito Cavia 2858 — sin costo</span>}
        </label>

        {form.con_envio && (
          <>
            <div className="vm-grid-2">
              <Field label="Departamento *">
                <select value={form.entrega_departamento} onChange={e => { setF('entrega_departamento', e.target.value); setF('entrega_barrio_id', '') }}>
                  {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Barrio *">
                <select value={form.entrega_barrio_id} onChange={e => setF('entrega_barrio_id', e.target.value)}>
                  <option value="">— Elegir —</option>
                  {barriosFiltrados.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
                {barrioElegido && <div className="vm-hint">Días de ruta: {(barrioElegido.dias || []).join(', ') || 'sin días asignados'}</div>}
              </Field>
            </div>
            <div className="vm-grid-2">
              <Field label="Fecha de entrega *">
                <input type="date" value={form.fecha_entrega} min={hoyISO()} onChange={e => setF('fecha_entrega', e.target.value)} />
                {fechaInfo && (
                  <div className={`vm-hint ${fechaInfo.enRuta ? 'ok' : 'warn'}`}>
                    {fechaInfo.enRuta ? `🟢 ${fechaInfo.diaSem} en ruta de ${fechaInfo.barrio}` : `🟡 ${fechaInfo.diaSem} fuera de ruta`}
                  </div>
                )}
              </Field>
            </div>
            <div className="vm-grid-2">
              <Field label="Dirección *"><input value={form.entrega_direccion} onChange={e => setF('entrega_direccion', e.target.value)} placeholder="Calle y número" /></Field>
              <Field label="Apto / referencia"><input value={form.entrega_apto} onChange={e => setF('entrega_apto', e.target.value)} placeholder="Apto 302" /></Field>
            </div>
            <Field label="Ventana horaria">
              <select value={form.ventana_horaria} onChange={e => setF('ventana_horaria', e.target.value)}>
                {VENTANAS.map(v => <option key={v.v} value={v.v}>{v.l}</option>)}
              </select>
            </Field>

            <Field label={`Costo de envío ${form.envio_override ? '· 🟡 manual' : '· auto'}`}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="number" min="0" step="1"
                  value={form.envio_override ? form.costo_envio_manual : costoEnvioAuto}
                  onChange={e => setForm(f => ({ ...f, envio_override: true, costo_envio_manual: Number(e.target.value) || 0 }))}
                  style={{ flex: 1 }} />
                {form.envio_override && (
                  <button className="vm-link" onClick={() => setF('envio_override', false)}>↻ Auto</button>
                )}
              </div>
              <div className="vm-hint">
                Envío LimpackFlex por zona del barrio · gratis en compras grandes · mismo criterio que la tienda
              </div>
            </Field>
          </>
        )}
      </section>

      {/* Facturación — SIEMPRE emite comprobante (e-Ticket o e-Factura) */}
      <section className="vm-card">
        <h3>🧾 Facturación</h3>
        <div className="vm-hint" style={{ marginBottom: 12 }}>Toda venta emite comprobante: e-Ticket (consumidor final) o e-Factura si es empresa (RUT).</div>
        <div className="vm-grid-2">
          <Field label="Tipo de comprobante">
            <select value={form.comprador_documento_tipo} onChange={e => setF('comprador_documento_tipo', e.target.value)}>
              <option value="ci">e-Ticket (consumidor final)</option>
              <option value="rut">e-Factura (empresa · RUT)</option>
            </select>
          </Field>
          {form.comprador_documento_tipo === 'rut' ? (
            <Field label="RUT (12 dígitos)">
              <input value={form.comprador_documento} onChange={e => setF('comprador_documento', e.target.value)} placeholder="212345670012" />
            </Field>
          ) : (
            <Field label="Cédula (para el ticket)">
              <div className="vm-hint" style={{ background: '#f1f5f9', color: '#475569' }}>
                {form.comprador_documento ? `Cédula: ${form.comprador_documento} (cargada en Cliente)` : 'Sin cédula = consumidor final. Cargala en Cliente si la quiere en el ticket.'}
              </div>
            </Field>
          )}
        </div>
        {form.comprador_documento_tipo === 'rut' && (
          <Field label="Razón social">
            <input value={form.facturacion_razon_social} onChange={e => setF('facturacion_razon_social', e.target.value)} placeholder="Empresa SA" />
          </Field>
        )}
        <Field label="Email para enviar el comprobante">
          <input type="email" value={form.facturacion_email} onChange={e => setF('facturacion_email', e.target.value)} placeholder="factura@ejemplo.com" />
        </Field>
      </section>

      {/* Pago */}
      <section className="vm-card">
        <h3>💳 Pago</h3>
        <div className="vm-pago-fijo">
          <div>
            <strong>🏦 Transferencia bancaria</strong>
            <div className="vm-cel-sub">Único método habilitado (evita comisión MP)</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="vm-hint" style={{ background: '#dcfce7', color: '#166534' }}>Pendiente confirmar</div>
          </div>
        </div>
        <Field label="Notas internas">
          <textarea rows={2} value={form.notas} onChange={e => setF('notas', e.target.value)} placeholder="Ej: conocido de Nico, paga a la semana, etc." />
        </Field>
      </section>

      {/* Desglose + descuento + botón crear */}
      <section className="vm-card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 10 }}>
          <Field label="Descuento global (%)">
            <input
              type="number" min="0" max="100" step="1"
              value={form.descuento_pct}
              onChange={e => setF('descuento_pct', e.target.value)}
              placeholder="0"
              style={{ maxWidth: 120 }}
            />
          </Field>
          <div className="vm-hint" style={{ marginBottom: 12 }}>
            Se aplica al precio de cada producto y queda marcado "(dto X%)" en el nombre que va en la factura. El envío no se descuenta.
          </div>
        </div>

        <div className="vm-desglose">
          <div className="vm-des-row"><span>Subtotal productos</span><span>{fmtMoney(subtotalBruto)}</span></div>
          {descuentoPct > 0 && (
            <div className="vm-des-row" style={{ color: '#b45309' }}>
              <span>🏷 Descuento {descuentoPct}%</span>
              <span>− {fmtMoney(descuentoMonto)}</span>
            </div>
          )}
          {descuentoPct > 0 && (
            <div className="vm-des-row"><span>Subtotal con descuento</span><span>{fmtMoney(subtotal)}</span></div>
          )}
          <div className="vm-des-row"><span>{form.con_envio ? `Envío ${fechaInfo?.enRuta ? '(en ruta)' : '(fuera de ruta)'}` : 'Envío (retira)'}</span><span>{costoEnvio > 0 ? `+ ${fmtMoney(costoEnvio)}` : 'gratis'}</span></div>
          <div className="vm-des-row vm-des-total"><span>Total a cobrar</span><span>{fmtMoney(total)}</span></div>
        </div>

        {error && <div className="vm-error">⚠ {error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="vm-btn-secondary" onClick={onCancelar} disabled={creando}>Cancelar</button>
          <button className="vm-btn-primary" onClick={crear} disabled={creando || carrito.length === 0}>
            {creando ? 'Creando…' : '✓ Confirmar venta manual'}
          </button>
        </div>
      </section>
    </div>
  )
}

// ────────────────────── HELPERS ──────────────────────
function Field({ label, children }) {
  return (
    <label className="vm-field">
      <span>{label}</span>
      {children}
    </label>
  )
}

function Estilos() {
  return (
    <style>{`
      .vm-page { max-width: 900px; margin: 0 auto; padding: 20px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .vm-page h1 { margin: 0 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .vm-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .vm-back:hover { color: #1A3461; }
      .muted { color: #94a3b8; font-size: 13px; }

      .vm-topbar { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }

      .vm-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 18px 20px; margin-bottom: 14px; }
      .vm-card h3 { margin: 0 0 12px; font-size: 14px; font-weight: 800; color: #1A3461; }

      .vm-check { display: flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 600; color: #1A3461; margin-bottom: 12px; cursor: pointer; }
      .vm-check input { width: 17px; height: 17px; margin: 0; accent-color: #00A8B5; }
      .vm-cli-search { margin-bottom: 12px; }
      .vm-cli-linked { display: flex; align-items: center; justify-content: space-between; gap: 10px; background: #ecfeff; border: 1px solid #a5f3fc; color: #155e75; border-radius: 8px; padding: 9px 12px; font-size: 13px; font-weight: 600; margin-bottom: 12px; }
      .vm-cli-linked--warn { background: #fffbeb; border-color: #fde68a; color: #92400e; }
      .vm-btn-mini { background: #fff; border: 1px solid #cbd5e1; color: #64748b; font-size: 12px; font-weight: 700; padding: 5px 10px; border-radius: 7px; cursor: pointer; font-family: inherit; }
      .vm-btn-mini:hover { border-color: #94a3b8; color: #1A3461; }
      .vm-chip-cli { display: inline-block; margin-left: 7px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: #00A8B5; background: #ecfeff; border: 1px solid #a5f3fc; padding: 1px 7px; border-radius: 999px; vertical-align: middle; }

      .vm-modal-bg { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; z-index: 200; overflow-y: auto; }
      .vm-modal { background: #fff; border-radius: 14px; padding: 20px; width: 100%; max-width: 520px; box-shadow: 0 20px 50px rgba(0,0,0,0.3); }
      .vm-modal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
      .vm-modal-head h3 { margin: 0; font-size: 16px; font-weight: 800; color: #1A3461; }
      .vm-modal-err { background: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; border-radius: 8px; padding: 9px 12px; font-size: 13px; margin-top: 10px; }
      .vm-modal-foot { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }

      .vm-field { display: block; margin-bottom: 12px; }
      .vm-field > span { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
      .vm-field input, .vm-field select, .vm-field textarea {
        width: 100%; padding: 9px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box;
      }
      .vm-field textarea { resize: vertical; min-height: 50px; }
      .vm-field input:focus, .vm-field select:focus, .vm-field textarea:focus { outline: none; border-color: #00A8B5; }
      .vm-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      @media (max-width: 640px) { .vm-grid-2 { grid-template-columns: 1fr; } }

      .vm-check { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #F8FAFC; border-radius: 8px; margin-bottom: 12px; cursor: pointer; }
      .vm-check input { width: 18px; height: 18px; cursor: pointer; }
      .vm-check span:first-of-type { font-weight: 700; color: #1A3461; }

      .vm-hint { font-size: 11.5px; color: #64748B; margin-top: 4px; padding: 4px 8px; background: #F8FAFC; border-radius: 6px; display: inline-block; }
      .vm-hint.ok { background: #DCFCE7; color: #14532D; }
      .vm-hint.warn { background: #FEF3C7; color: #78350F; }
      .vm-error { background: #FEE2E2; border-left: 3px solid #DC2626; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #991B1B; font-size: 13px; margin: 12px 0; }

      .vm-search-results { border: 1px solid #E2E8F0; border-radius: 8px; max-height: 280px; overflow-y: auto; margin-top: 6px; background: #fff; }
      .vm-search-item {
        width: 100%; display: flex; justify-content: space-between; align-items: center;
        padding: 10px 12px; border: 0; border-bottom: 1px solid #F1F5F9; background: #fff;
        cursor: pointer; text-align: left; font-family: inherit; font-size: 13px; color: #1A3461;
      }
      .vm-search-item:hover { background: #F8FAFC; }
      .vm-search-item:last-child { border-bottom: 0; }

      .vm-table { width: 100%; border-collapse: collapse; font-size: 13px; }
      .vm-table thead th { background: #F8FAFC; padding: 9px 12px; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.03em; text-align: left; white-space: nowrap; border-bottom: 1px solid #E2E8F0; }
      .vm-table tbody td { padding: 11px 12px; border-bottom: 1px solid #EEF2F6; vertical-align: middle; white-space: nowrap; font-variant-numeric: tabular-nums; }
      .vm-table tbody tr:last-child td { border-bottom: 0; }
      .vm-table td.vm-cel-cliente { white-space: normal; min-width: 190px; }
      .vm-table tbody tr:hover { background: #F8FAFC; cursor: pointer; }
      .vm-cel-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; white-space: nowrap; }
      .vm-table-wrap { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; overflow-x: auto; box-shadow: 0 1px 2px rgba(16,24,40,0.04); }

      .vm-loading { padding: 40px; text-align: center; color: #64748B; }
      .vm-empty { padding: 60px 20px; text-align: center; color: #64748B; }
      .vm-empty h3 { color: #1A3461; margin: 12px 0 6px; }

      .vm-pago-fijo { display: flex; justify-content: space-between; align-items: center; padding: 12px 14px; background: #F0FDFA; border: 1px solid #A7F3D0; border-radius: 10px; margin-bottom: 12px; }

      .vm-desglose { background: #F8FAFC; border-radius: 8px; padding: 12px 14px; }
      .vm-des-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13.5px; color: #475569; }
      .vm-des-row span:last-child { font-variant-numeric: tabular-nums; font-weight: 600; color: #1A3461; }
      .vm-des-total { border-top: 1px solid #E2E8F0; padding-top: 8px; margin-top: 4px; font-weight: 800; font-size: 15px; }
      .vm-des-total span:last-child { color: #00A8B5; font-size: 17px; }

      .vm-btn-primary, .vm-btn-secondary { padding: 10px 18px; border-radius: 8px; font-size: 13.5px; font-weight: 700; font-family: inherit; cursor: pointer; border: none; }
      .vm-btn-primary { background: #1A3461; color: #fff; }
      .vm-btn-primary:hover:not(:disabled) { background: #15294d; }
      .vm-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
      .vm-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .vm-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }

      .vm-link { background: transparent; border: 0; color: #00A8B5; cursor: pointer; font-family: inherit; font-size: 12.5px; font-weight: 700; }
      .vm-link:hover { text-decoration: underline; }
      .vm-link-danger { background: transparent; border: 0; color: #DC2626; cursor: pointer; font-size: 14px; padding: 4px 6px; }
      .vm-link-danger:hover { color: #7F1D1D; }

      .vm-btn-pagar {
        padding: 6px 12px; background: #16A34A; color: #fff; border: 0; border-radius: 8px;
        font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; white-space: nowrap;
      }
      .vm-btn-pagar:hover:not(:disabled) { background: #15803d; }
      .vm-btn-pagar:disabled { opacity: 0.6; cursor: not-allowed; }

      .vm-exito { max-width: 480px; margin: 60px auto; text-align: center; padding: 32px; background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; }
      .vm-exito h2 { margin: 8px 0; font-size: 22px; font-weight: 900; }
      .vm-exito code { background: #F1F5F9; padding: 2px 6px; border-radius: 4px; font-size: 11px; }
    `}</style>
  )
}
